import { inject, Injectable, signal } from '@angular/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { LoggingService } from './logging.service';
import { PlatformService } from './platform.service';

@Injectable({ providedIn: 'root' })
export class CrashlyticsService {
  private readonly platformService = inject(PlatformService);
  private readonly loggingService = inject(LoggingService);

  readonly isInitialized = signal(false);

  async initialize(): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      this.isInitialized.set(true);
      return;
    }

    try {
      await FirebaseCrashlytics.setEnabled({ enabled: true });

      const deviceDetails = await this.platformService.ensureDeviceDetails();
      if (deviceDetails) {
        await FirebaseCrashlytics.setCustomKey({ key: 'platform', value: deviceDetails.platform, type: 'string' });
        await FirebaseCrashlytics.setCustomKey({ key: 'app_version', value: deviceDetails.appVersion, type: 'string' });
        await FirebaseCrashlytics.setCustomKey({ key: 'app_build', value: deviceDetails.appBuild, type: 'string' });
        await FirebaseCrashlytics.setCustomKey({ key: 'os_version', value: deviceDetails.osVersion, type: 'string' });
        await FirebaseCrashlytics.setCustomKey({ key: 'device_model', value: deviceDetails.model, type: 'string' });
      }

      const crashResult = await FirebaseCrashlytics.didCrashOnPreviousExecution();
      if (crashResult.crashed) {
        this.loggingService.logWarning('CrashlyticsService: App crashed on previous execution');
      }

      this.isInitialized.set(true);
    } catch (error) {
      this.loggingService.logException(error, 'CrashlyticsService: Initialization failed');
      this.isInitialized.set(true);
    }
  }

  async setUserId(userId: string): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      return;
    }

    try {
      await FirebaseCrashlytics.setUserId({ userId });
    } catch (error) {
      this.loggingService.logException(error, 'CrashlyticsService: Failed to set user ID');
    }
  }

  async recordException(error: Error, context?: string): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      return;
    }

    try {
      await FirebaseCrashlytics.log({ message: context ? `[${context}] ${error.message}` : error.message });

      await FirebaseCrashlytics.recordException({
        message: error.message,
        stacktrace: this.parseStackTrace(error),
      });
    } catch (recordError) {
      this.loggingService.logException(recordError, 'CrashlyticsService: Failed to record exception');
    }
  }

  async log(message: string): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      return;
    }

    try {
      await FirebaseCrashlytics.log({ message });
    } catch (error) {
      this.loggingService.logException(error, 'CrashlyticsService: Failed to log message');
    }
  }

  async setCustomKey(key: string, value: string | number | boolean): Promise<void> {
    if (!this.platformService.isNativeMobile()) {
      return;
    }

    try {
      const type = typeof value === 'string' ? 'string' : typeof value === 'boolean' ? 'boolean' : 'double';
      await FirebaseCrashlytics.setCustomKey({ key, value, type });
    } catch (error) {
      this.loggingService.logException(error, 'CrashlyticsService: Failed to set custom key');
    }
  }

  private parseStackTrace(error: Error): Array<{ lineNumber?: number; fileName?: string; functionName?: string }> {
    if (!error.stack) {
      return [];
    }

    const lines = error.stack.split('\n').slice(1);
    return lines
      .map((line) => {
        const v8Match = line.match(/at\s+(.+?)\s+\((.+?):(\d+):\d+\)/);
        if (v8Match) {
          return {
            functionName: v8Match[1],
            fileName: v8Match[2],
            lineNumber: parseInt(v8Match[3], 10),
          };
        }
        const jscMatch = line.match(/(.*?)@(.+?):(\d+):\d+/);
        if (jscMatch) {
          return {
            functionName: jscMatch[1] || '(anonymous)',
            fileName: jscMatch[2],
            lineNumber: parseInt(jscMatch[3], 10),
          };
        }
        return null;
      })
      .filter((frame): frame is { functionName: string; fileName: string; lineNumber: number } => frame !== null)
      .slice(0, 10);
  }
}
